"use client";

import { TIndustriesConfig } from "@/app/config";
import { TrendingUp } from "lucide-react";
import { motion } from "motion/react";
import React from "react";

// Enhanced animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
      duration: 0.6
    }
  }
};
const metricsGridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.3
    }
  }
};


export default function MajorPlatformPerformance({config}:{config:TIndustriesConfig['majorPlatformPerformance']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-gray-50 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={containerVariants}
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
      />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94] // Custom easing
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.1 
            }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.2 
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Platform Cards */}
        <motion.div 
          className="space-y-6"
          variants={containerVariants}
        >
          {config.platforms.map((platform, index) => (
            <motion.div
              key={index}
              className={`${platform.bgColor} ${platform.borderColor} border rounded-2xl p-6 cursor-pointer relative overflow-hidden`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 60,
                  scale: 0.9,
                  rotateX: -15
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  rotateX: 0,
                  transition: {
                    duration: 0.7,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    staggerChildren: 0.1,
                    delayChildren: 0.2
                  }
                }
              }}
              whileHover={{
                scale: 1.03,
                y: -8,
                rotateX: 2,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
              style={{ perspective: 1000 }}
            >
              {/* Background glow effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"
                variants={{
                  hidden: { opacity: 0, scale: 0.8 },
                  visible: {
                    opacity: 0.1,
                    scale: 1,
                    transition: {
                      duration: 1,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    opacity: 0.2,
                    scale: 1.1,
                    transition: {
                      duration: 0.3
                    }
                  }
                }}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              />

              {/* Animated border highlight */}
              <motion.div
                className="absolute inset-0 rounded-2xl"
                initial={{ background: "transparent" }}
                whileHover={{
                  background: "linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))",
                }}
                transition={{ duration: 0.3 }}
              />

              {/* Platform Header */}
              <div className="flex items-center justify-between mb-6 relative z-10">
                <motion.div
                  variants={{
                    hidden: { opacity: 0, x: -30 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  <motion.h3 
                    className={`text-xl font-semibold ${platform.nameColor} mb-1`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: index * 0.1, 
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    {platform.name}
                  </motion.h3>
                  <motion.p 
                    className="text-gray-600 text-sm"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: index * 0.1 + 0.1, 
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    {platform.subtitle}
                  </motion.p>
                </motion.div>
                
                <motion.div 
                  className="flex items-center gap-2"
                  variants={ {
                    hidden: { opacity: 0, x: 30, scale: 0.8 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      scale: 1,
                      transition: {
                        duration: 0.6,
                        ease: "backOut",
                        delay: 0.1
                      }
                    },
                    hover: {
                      scale: 1.1,
                      rotate: [0, -5, 5, 0],
                      transition: {
                        duration: 0.4,
                        ease: "easeInOut"
                      }
                    }
                  }}
                  whileHover="hover"
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: [0, 180, 360]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "loop",
                      ease: "linear"
                    }}
                  >
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  </motion.div>
                  <motion.span 
                    className="text-sm text-green-600 font-medium"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.1 + 0.3,
                      duration: 0.4,
                      ease: "backOut"
                    }}
                  >
                    {platform.status}
                  </motion.span>
                </motion.div>
              </div>

              {/* Metrics Grid */}
              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10"
                variants={metricsGridVariants}
              >
                {platform.metrics.map((metric, metricIndex) => (
                  <motion.div 
                    key={metricIndex} 
                    className="text-center group relative"
                    variants={{
                      hidden: { 
                        opacity: 0, 
                        y: 30,
                        scale: 0.8
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          ease: "backOut"
                        }
                      },
                      hover: {
                        scale: 1.08,
                        y: -5,
                        transition: {
                          duration: 0.2,
                          ease: "easeOut"
                        }
                      }
                    }}
                    whileHover="hover"
                  >
                    {/* Metric background highlight */}
                    <motion.div
                      className="absolute inset-0 bg-white/30 rounded-lg -m-2"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileHover={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                    
                    <motion.p 
                      className="text-gray-600 text-sm mb-1 relative z-10"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ 
                        delay: (index * 0.15) + (metricIndex * 0.05) + 0.4,
                        duration: 0.4
                      }}
                    >
                      {metric.label}
                    </motion.p>
                    
                    <motion.p 
                      className={`font-bold text-lg ${metric.color} relative z-10`}
                      variants={ {
                        hidden: { 
                          opacity: 0, 
                          scale: 0.3,
                          rotateY: -180
                        },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          rotateY: 0,
                          transition: {
                            duration: 0.6,
                            ease: "backOut",
                            delay: 0.1
                          }
                        },
                        hover: {
                          scale: 1.15,
                          transition: {
                            duration: 0.2,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ 
                        delay: (index * 0.15) + (metricIndex * 0.05) + 0.5
                      }}
                    >
                      {metric.value}
                    </motion.p>

                    {/* Floating animation for metric values */}
                    <motion.div
                      className="absolute inset-0"
                      animate={{
                        y: [0, -2, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                        delay: metricIndex * 0.2
                      }}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}